import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import useColors from 'hooks/useColors';
import { AppView, Button, CollapsableContainer, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { formatPlural } from 'utils';
import { SpaceUnavailableFlow } from 'utils/types/navigation';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Text from 'design-system/Text';
import { setPotSpace } from 'features/pots/actions';
import { setTradingAccountSpace } from 'actions/invest';
import { forceRefreshAnalytics, getBankConnections, getCategories, getFeed, } from 'actions';
import BottomPanel from 'design-system/BottomPanel';
import { selectDefaultSpace } from './reducers/selectors';
import { editSpaceInfoSuccess, fetchSpacesAction, setAccountSpace, } from './actions';
import { AccountItem } from './components';
import { isWeb } from '../../constants';
import { useMySpaceAccounts } from './hooks/useMySpaceAccounts';
const SpaceUnavailableMoveAccountsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { flow, spaceInfo } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const { mySpaceAccounts } = useMySpaceAccounts();
    const defaultSpace = useAppSelector(selectDefaultSpace);
    const userId = useAppSelector(selectUserId);
    const currentSpace = useMemo(() => mySpaceAccounts.find(({ space }) => space.id === spaceInfo.space.id), [mySpaceAccounts, spaceInfo.space.id]);
    const [isMovingAccounts, setIsMovingAccounts] = useState(false);
    const onMoveAccounts = useCallback(async () => {
        if (flow ===
            SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS) {
            navigation.navigateDeprecated('SpaceUnavailableSelectMoveAccounts', {
                spaceInfo,
            });
            return;
        }
        if (!defaultSpace?.id || !currentSpace)
            return;
        setIsMovingAccounts(true);
        const promises = [];
        promises.push(...currentSpace.accounts.map((account) => {
            if ('productId' in account) {
                return dispatch(setPotSpace(String(account.id), defaultSpace.id));
            }
            if ('isEmmaInvestmentAccount' in account) {
                return dispatch(setTradingAccountSpace(defaultSpace.id, account.id));
            }
            return dispatch(setAccountSpace(account.id, defaultSpace.id));
        }));
        try {
            await Promise.all(promises);
            dispatch(fetchSpacesAction());
            dispatch(getBankConnections());
            dispatch(forceRefreshAnalytics());
            dispatch(getCategories());
            dispatch(getFeed());
            dispatch(editSpaceInfoSuccess(defaultSpace.id));
            dispatch(editSpaceInfoSuccess(spaceInfo.space.id));
            setIsMovingAccounts(false);
            navigation.pop(2);
            navigation.navigateDeprecated('SpaceUnavailableAccountsMoved', {
                spaceInfo,
            });
        }
        catch {
            setIsMovingAccounts(false);
        }
    }, [currentSpace, defaultSpace?.id, flow, spaceInfo]);
    const onLeaveAccounts = useCallback(() => {
        navigation.pop(2);
        navigation.navigateDeprecated('SpaceUnavailableLeaveAccounts', {
            spaceInfo,
        });
    }, [spaceInfo]);
    const spaces = useMemo(() => {
        if (flow ===
            SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS) {
            return mySpaceAccounts.filter(({ space }) => space.createdBy === userId && !space.isDefault);
        }
        return [
            {
                space: spaceInfo.space,
                accounts: currentSpace?.accounts || [],
            },
        ];
    }, [
        mySpaceAccounts,
        currentSpace?.accounts,
        flow,
        spaceInfo.space,
        userId,
    ]);
    const secondaryButtonTitle = useMemo(() => getSecondaryButtonTitle(flow, spaces[0].accounts.length), [flow, spaces]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
            <ScreenInfoHeader title="Do you want to move your accounts back to your Private Space?" subTitle={<Subtitle flow={flow} spaceName={spaceInfo.space.name}/>}/>
            {flow ===
            SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS && (<Text Text-16 Secondary style={styles.sectionTitle}>
                Accounts in your custom spaces
              </Text>)}
            {/* TODO: use FlashList/FlatList */}
            {spaces.map((item) => item.accounts.length > 0 ? (<CollapsableContainer key={item.space.id} title={item.space.name} style={styles.itemContainer} isCollapsedByDefault={false}>
                  {item.accounts.map((account) => (<AccountItem hideSpaceUserAvatar key={account.id} account={account} disabled/>))}
                </CollapsableContainer>) : null)}
          </Animated.ScrollView>
          <BottomPanel>
            <Button brandReversed title={secondaryButtonTitle} onPress={onMoveAccounts} isFetching={isMovingAccounts}/>
            <Spacer.H8 />
            <Button brand title="Leave them for now" onPress={onLeaveAccounts}/>
          </BottomPanel>
        </AppView>);
}, ({ colors }) => ({
    title: 'Move accounts',
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    sectionTitle: {
        marginHorizontal: rem(16),
        marginTop: rem(8),
        marginBottom: rem(16),
    },
    itemContainer: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
});
export default SpaceUnavailableMoveAccountsScreen;
const Subtitle = ({ flow, spaceName, }) => {
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return (<>
          <Text TextThin-16 Secondary>
            These accounts aren’t visible in your Feed because they’re in Spaces
            you’re no longer using.
          </Text>
          <Spacer h={10}/>
          <Text TextThin-16 Secondary>
            To see them again, move them to your Private Space — or leave them
            for now and upgrade your plan later to keep using Spaces.
          </Text>
        </>);
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS:
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS:
            return (<>
          <Text TextThin-16 Secondary>
            These accounts aren’t visible in your Feed because the {spaceName}{' '}
            Space is no longer available.
          </Text>
          <Spacer h={10}/>
          <Text TextThin-16 Secondary>
            To see them again, move them to your Private Space — or leave them
            for now and wait for the owner to upgrade.
          </Text>
        </>);
        default:
            return null;
    }
};
const getSecondaryButtonTitle = (flow, accountsLength) => {
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return 'Move accounts';
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS:
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS:
            return `Move ${accountsLength} ${formatPlural('account', 'accounts', accountsLength)} to Private space`;
        default:
            return '';
    }
};
